import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, toggleOpenFieldSection } from '../store';
import { RepoEarlyCloseOut, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface RepoEarlyCloseOutSectionProps {
    earlyCloseOut: RepoEarlyCloseOut;
    previousEarlyCloseOut: RepoEarlyCloseOut | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    nettingEngineFields?: string[];
}

export const RepoEarlyCloseOutSection: React.FC<RepoEarlyCloseOutSectionProps> = ({ earlyCloseOut, previousEarlyCloseOut, isEditing, isUpdating, fieldsUpdated, nettingEngineFields }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'earlyCloseOut';
    const { aetEnforceable, aetRequired, aetAdvisable, optionalAetEnforceable, closeOutNettingEnforceable, baseCurrencyCashPayment, securitiesHeldOutsideJurisdiction, crossProductCrossAgreementImpact } = earlyCloseOut;

    const getPreviousField = useCallback((fieldId: keyof RepoEarlyCloseOut) => !isNull(previousEarlyCloseOut) && previousEarlyCloseOut[fieldId] || null, [previousEarlyCloseOut]);

    return (
        <SectionWrapper id={sectionId} label='Early Close-Out' isEditing={isEditing} isUpdating={isUpdating} nettingEngineFields={nettingEngineFields}>
            <div className={styles.earlyCloseOutSectionWrapper}>
                <div className={styles.sectionHeaderLabel}>Under the laws of the jurisdiction under consideration:</div>
                <SingleField
                    field={aetEnforceable}
                    previousField={getPreviousField('aetEnforceable')}
                    fieldId='aetEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={aetRequired}
                    previousField={getPreviousField('aetRequired')}
                    fieldId='aetRequired'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={[]}
                    overrideDisabled
                    guidanceNotes={['This field is derived from the information in the counterparty coverage section, but details can still be added here.']}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={aetAdvisable}
                    previousField={getPreviousField('aetAdvisable')}
                    fieldId='aetAdvisable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={optionalAetEnforceable}
                    previousField={getPreviousField('optionalAetEnforceable')}
                    fieldId='optionalAetEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={closeOutNettingEnforceable}
                    previousField={getPreviousField('closeOutNettingEnforceable')}
                    fieldId='closeOutNettingEnforceable'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={baseCurrencyCashPayment}
                    previousField={getPreviousField('baseCurrencyCashPayment')}
                    fieldId='baseCurrencyCashPayment'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={securitiesHeldOutsideJurisdiction}
                    previousField={getPreviousField('securitiesHeldOutsideJurisdiction')}
                    fieldId='securitiesHeldOutsideJurisdiction'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={crossProductCrossAgreementImpact}
                    previousField={getPreviousField('crossProductCrossAgreementImpact')}
                    fieldId='crossProductCrossAgreementImpact'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
            </div>
        </SectionWrapper>
    );
};
